import React from "react"
import { graphql, Link, useStaticQuery } from "gatsby"
import Layout from "../components/layout"
import { Container, Grid, Header, Button, Segment } from "semantic-ui-react"
import ShareFooter from "../components/ShareFooter"
import ShareHeader from "../components/ShareHeader"
import DesktopMenu from "../components/DesktopMenu"
import JoinNewsletterModal from "../components/Shared/JoinNewsletterModal"
import { Media, MediaContextProvider } from "../utils/media"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

const cssAppliedContent = body => `
  <div>
  <style>
    img {
      max-height: 40em;
      width: 100%;
      margin: 0 auto;
      background-position: center;
      background-size: cover;
      background-repeat: no-repeat;
      text-align: center;
      display: block; 
    }
  </style>
  ${body}
  <div>
`

const Post = props => {
  const { previous, next, keywords, description } = props.pageContext
  const { url, data } = props.data.prismicBlogPost
  const { title, header_image, author, created_at, upsell, content } = data

  const socialConfig = {
    twitterHandle: "@artcollectionio",
    config: {
      url,
      title: data.title.text,
    },
    tags: ["#art, #artcollectionio"],
  }

  const elements = [<DesktopMenu />]

  return (
    <Layout
      title={title.text}
      description={description || content.text.slice(0, 200)}
      keywords={keywords}
      image={header_image.url}
    >
      <MediaContextProvider>
        <Media lessThan={"tablet"}>
          {React.Children.map(elements, child =>
            React.cloneElement(child, { isMobile: true }),
          )}
        </Media>
        <Media greaterThanOrEqual={"tablet"}>
          {React.Children.map(elements, child =>
            React.cloneElement(child, { isMobile: false }),
          )}
        </Media>
      </MediaContextProvider>

      <Container>
        <Grid style={{ paddingTop: "6em", paddingBottom: "6em" }} centered>
          <Grid.Row columns={1} centered>
            <Grid.Column>
              <Header>
                <Header.Content as={"h1"}>{title.text}</Header.Content>
                <Header.Subheader>
                  {author && (
                    <strong>
                      <a
                        className={"article-author"}
                        href={author.document.data.link.url}
                      >
                        {author.document.data.name.text}
                      </a>
                    </strong>
                  )}
                </Header.Subheader>
                <Header.Subheader
                  content={new Date(created_at).toDateString()}
                />
              </Header>
              <ShareHeader socialConfig={socialConfig} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1} centered>
            <Grid.Column>
              <GatsbyImage
                image={getImage(header_image)}
                alt={header_image.alt}
                style={{
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  textAlign: "center",
                  display: "block",
                }}
              />
              {header_image.copyright && (
                <Header
                  size="tiny"
                  style={{ textAlign: "center", marginTop: "1em" }}
                  content={header_image.copyright}
                />
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column
              dangerouslySetInnerHTML={{
                __html: cssAppliedContent(data.content.html),
              }}
            />
          </Grid.Row>
          {upsell && (
            <Grid.Row columns={1}>
              <Grid.Column>
                <Segment>
                  <Header
                    size="large"
                    content={upsell.document.data.title.text}
                    subheader={upsell.document.data.subtitle.text}
                  />
                  {upsell.document.data.action_text && (
                    <Button
                      primary
                      size="large"
                      as={Link}
                      to={upsell.document.data.link.url}
                    >
                      {upsell.document.data.action_text.text}
                    </Button>
                  )}
                </Segment>
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row columns={1}>
            <Grid.Column>
              <nav>
                <ul
                  style={{
                    display: `flex`,
                    flexWrap: `wrap`,
                    justifyContent: `space-between`,
                    listStyle: `none`,
                    padding: 0,
                  }}
                >
                  <li>
                    {previous && (
                      <Link to={previous.url} rel="prev">
                        ← {previous.data.title.text}
                      </Link>
                    )}
                  </li>
                  <li>
                    {next && (
                      <Link to={next.url} rel="next">
                        {next.data.title.text} →
                      </Link>
                    )}
                  </li>
                </ul>
              </nav>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <ShareFooter socialConfig={socialConfig} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <JoinNewsletterModal />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    </Layout>
  )
}

export default Post

export const pageQuery = graphql`
  query PostBySlug($uid: String!) {
    prismicBlogPost(uid: { eq: $uid }) {
      uid
      url
      data {
        created_at
        author {
          id
          document {
            ... on PrismicAuthor {
              uid
              data {
                name {
                  text
                  html
                }
                link {
                  url
                }
              }
            }
          }
        }
        upsell {
          uid
          document {
            ... on PrismicUpsell {
              data {
                title {
                  text
                }
                subtitle {
                  text
                }
                action_text {
                  text
                }
                link {
                  url
                }
              }
            }
          }
        }
        header_image {
          url
          alt
          copyright
          gatsbyImageData
        }
        title {
          text
        }
        content {
          text
          html
        }
      }
    }
  }
`
